'use client'

import { useState, useEffect } from 'react'
import Link from 'next/link'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent } from '@/components/ui/card'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { PromoCodeCapture } from '@/components/promo-code-capture'
import { SystemMessageBanner } from '@/components/system-message'
import { trpc } from '@/lib/trpc'
import { useDebounce } from '@/hooks/use-debounce'
import {
  Search,
  Radio,
  ArrowLeft,
  Users,
  Music,
  Sparkles,
  CheckCircle2,
  Loader2,
  MapPin,
  Zap,
  Heart,
} from 'lucide-react'
import { getPlaceholderProfileUrl } from '@/lib/placeholders'
import { cn } from '@/lib/utils'
import { trackArtistDiscovery } from '@/lib/analytics'
import { useTranslations } from 'next-intl'

export default function DiscoverPage() {
  const t = useTranslations('discover')
  const [searchQuery, setSearchQuery] = useState('')
  const debouncedQuery = useDebounce(searchQuery, 300)

  // Fetch live artists
  const { data: liveArtists, isLoading: liveLoading } = trpc.artist.getLiveArtists.useQuery(
    { limit: 12 }
  )

  // Fetch featured artists (when not searching)
  const { data: featuredArtists, isLoading: featuredLoading } = trpc.artist.getFeaturedArtists.useQuery(
    { limit: 8 },
    { enabled: !debouncedQuery }
  )

  // Search artists (only when there's a query)
  const { data: searchResults, isLoading: searchLoading } = trpc.artist.searchArtists.useQuery(
    { query: debouncedQuery, limit: 20 },
    { enabled: debouncedQuery.length >= 2 }
  )

  const isSearching = debouncedQuery.length >= 2
  const showResults = isSearching && searchResults

  // Track discover page visit on mount
  useEffect(() => {
    trackArtistDiscovery('discover_page')
  }, [])

  return (
    <>
      {/* Capture promo/referral codes from URL and store in cookie */}
      <PromoCodeCapture />
      
      {/* System Messages */}
      <div className="container mx-auto px-4 pt-4">
        <SystemMessageBanner targetLocation="discovery" />
      </div>

      {/* Hero Section */}
      <section className="relative py-16 px-4 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-violet-600/5 via-transparent to-transparent" />
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-violet-600/10 rounded-full blur-[128px]" />
        <div className="absolute top-0 right-1/4 w-96 h-96 bg-fuchsia-600/10 rounded-full blur-[128px]" />
        
        <div className="container mx-auto max-w-4xl relative">
          <div className="text-center mb-8">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              {t('title')}
            </h1>
            <p className="text-lg text-zinc-400 max-w-2xl mx-auto">
              {t('subtitle')}
            </p>
          </div>

          {/* Search Bar */}
          <div className="relative max-w-xl mx-auto">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-zinc-500" />
            <Input
              type="text"
              placeholder={t('searchPlaceholder')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full h-14 pl-12 pr-4 text-lg bg-zinc-900/80 border-zinc-700 text-white placeholder:text-zinc-500 rounded-xl focus:border-violet-500 focus:ring-violet-500/20"
            />
            {searchLoading && (
              <Loader2 className="absolute right-4 top-1/2 -translate-y-1/2 h-5 w-5 text-violet-400 animate-spin" />
            )}
          </div>

          {/* Quick tip */}
          <p className="text-center text-sm text-zinc-500 mt-4">
            <Sparkles className="inline h-3 w-3 mr-1" />
            {t.rich('tip', { accent: (chunks) => <span className="text-violet-400">{chunks}</span> })}
          </p>
        </div>
      </section>

      {/* Search Results */}
      {showResults && (
        <section className="py-8 px-4">
          <div className="container mx-auto max-w-6xl">
            <div className="flex items-center gap-2 mb-6">
              <Search className="h-5 w-5 text-zinc-400" />
              <h2 className="text-xl font-semibold text-white">
                {t('resultsFor', { query: debouncedQuery })}
              </h2>
              <span className="text-sm text-zinc-500">
                {t('foundCount', { count: searchResults.length })}
              </span>
            </div>

            {searchResults.length === 0 ? (
              <Card className="border-zinc-800 bg-zinc-900/50">
                <CardContent className="p-12 text-center">
                  <Music className="h-12 w-12 text-zinc-600 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-white mb-2">{t('noArtistsFound')}</h3>
                  <p className="text-zinc-400 max-w-md mx-auto">
                    {t('noArtistsFoundDescription', { query: debouncedQuery })}
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {searchResults.map((artist) => (
                  <ArtistCard
                    key={artist.id}
                    artist={artist}
                    isLive={artist.isLive}
                    liveEvent={artist.liveEvent}
                  />
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {/* Live Now Section - Only shown when there are live artists */}
      {!isSearching && !liveLoading && liveArtists && liveArtists.length > 0 && (
        <section className="py-12 px-4 relative overflow-hidden">
          <div className="container mx-auto max-w-6xl">
            <div className="text-center mb-8">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-red-500/10 border border-red-500/30 mb-3">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500"></span>
                </span>
                <span className="text-xs text-red-300 uppercase tracking-wider font-medium">{t('liveNow')}</span>
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-white">
                {t('artistsPerformingRightNow')}
              </h2>
            </div>

            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {liveArtists.map(({ artist, event }) => (
                <LiveArtistCard
                  key={artist.id}
                  artist={artist}
                  event={event}
                />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Featured Artists Section */}
      {!isSearching && (
        <section className="py-12 px-4 relative overflow-hidden">
          <div className="container mx-auto max-w-6xl">
            <div className="text-center mb-8">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-violet-500/10 border border-violet-500/30 mb-3">
                <Sparkles className="h-3 w-3 text-violet-400" />
                <span className="text-xs text-violet-300 uppercase tracking-wider font-medium">{t('featured')}</span>
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-white">
                {t('activeArtists')}
              </h2>
              <p className="text-zinc-400 mt-2">
                {t('activeArtistsDescription')}
              </p>
            </div>

            {featuredLoading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="h-8 w-8 text-violet-400 animate-spin" />
              </div>
            ) : featuredArtists && featuredArtists.length > 0 ? (
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                {featuredArtists.map((artist) => (
                  <FeaturedArtistCard key={artist.id} artist={artist} />
                ))}
              </div>
            ) : (
              <Card className="border-zinc-800 bg-zinc-900/50 max-w-2xl mx-auto">
                <CardContent className="p-12 text-center">
                  <Users className="h-12 w-12 text-zinc-600 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-white mb-2">{t('noFeaturedArtists')}</h3>
                  <p className="text-zinc-400 max-w-md mx-auto">
                    {t('beOneOfTheFirst')}
                  </p>
                  <Link href="/signup" className="mt-4 inline-block">
                    <Button className="bg-gradient-to-r from-violet-600 to-fuchsia-600">
                      {t('becomeAnArtist')}
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            )}
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-16 px-4 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-t from-violet-600/5 via-transparent to-transparent" />
        <div className="container mx-auto max-w-3xl text-center relative">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
            {t('areYouAPerformer')}
          </h2>
          <p className="text-zinc-400 mb-8 max-w-xl mx-auto">
            {t('ctaDescription')}
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link href="/signup">
              <Button size="lg" className="bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-700 hover:to-fuchsia-700">
                <Zap className="h-4 w-4 mr-2" />
                {t('startFreeToday')}
              </Button>
            </Link>
            <Link href="/">
              <Button size="lg" variant="outline" className="border-zinc-700 hover:bg-zinc-800">
                {t('learnMoreAboutIperform')}
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 border-t border-zinc-800">
        <div className="container mx-auto max-w-6xl">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <Link href="/" className="flex items-center gap-2 text-zinc-400 hover:text-white transition-colors">
              <ArrowLeft className="h-4 w-4" />
              {t('backToIperform')}
            </Link>
            <div className="flex items-center gap-6 text-sm text-zinc-500">
              <Link href="/terms" className="hover:text-zinc-300 transition-colors">{t('terms')}</Link>
              <Link href="/privacy" className="hover:text-zinc-300 transition-colors">{t('privacy')}</Link>
              <span>{t('copyright', { year: new Date().getFullYear() })}</span>
            </div>
          </div>
        </div>
      </footer>
    </>
  )
}

// Artist Card for search results
function ArtistCard({
  artist,
  isLive,
  liveEvent
}: {
  artist: {
    id: string
    name: string
    handle: string
    bio: string | null
    profileImageUrl: string | null
    verifiedAt: Date | null
  }
  isLive: boolean
  liveEvent: { id: string; title: string; venue: string | null } | null
}) {
  const t = useTranslations('discover')
  return (
    <Link href={`/${artist.handle}`}>
      <Card className={cn(
        "border-zinc-800 bg-zinc-900/50 hover:bg-zinc-800/50 transition-all duration-300 group cursor-pointer h-full",
        isLive && "border-red-500/30 hover:border-red-500/50 hover:shadow-[0_0_30px_rgba(239,68,68,0.15)]"
      )}>
        <CardContent className="p-5">
          <div className="flex items-start gap-4">
            <Avatar className="h-14 w-14 ring-2 ring-zinc-700 group-hover:ring-violet-500/50 transition-all">
              <AvatarImage src={artist.profileImageUrl || getPlaceholderProfileUrl(artist.id, artist.name)} />
              <AvatarFallback className="bg-gradient-to-br from-violet-600 to-fuchsia-600 text-white text-lg">
                {artist.name.charAt(0).toUpperCase()}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <h3 className="font-semibold text-white truncate">{artist.name}</h3>
                {artist.verifiedAt && (
                  <CheckCircle2 className="h-4 w-4 text-violet-400 shrink-0" />
                )}
              </div>
              <p className="text-sm text-zinc-500 truncate">@{artist.handle}</p>
              {artist.bio && (
                <p className="text-sm text-zinc-400 mt-2 line-clamp-2">{artist.bio}</p>
              )}
            </div>
          </div>
          {isLive && liveEvent && (
            <div className="mt-4 flex items-center gap-2 text-xs">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500"></span>
              </span>
              <span className="text-red-400 font-medium">{t('live')}</span>
              <span className="text-zinc-400 truncate">{liveEvent.title}</span>
            </div>
          )}
        </CardContent>
      </Card>
    </Link>
  )
}

// Live Artist Card
function LiveArtistCard({ 
  artist, 
  event 
}: { 
  artist: {
    id: string
    name: string
    handle: string
    bio: string | null
    profileImageUrl: string | null
    isVerified: boolean
  }
  event: {
    id: string
    title: string
    venue: string | null
    viewerCount: number
    reactionCount: number
  }
}) {
  const t = useTranslations('discover')
  return (
    <Link href={`/${artist.handle}`}>
      <Card className="border-red-500/30 bg-gradient-to-br from-zinc-900 to-zinc-900/50 hover:border-red-500/50 hover:shadow-[0_0_30px_rgba(239,68,68,0.15)] transition-all duration-300 group cursor-pointer h-full">
        <CardContent className="p-5">
          <div className="flex items-start gap-4 mb-4">
            <div className="relative">
              <Avatar className="h-14 w-14 ring-2 ring-red-500/50">
                <AvatarImage src={artist.profileImageUrl || getPlaceholderProfileUrl(artist.id, artist.name)} />
                <AvatarFallback className="bg-gradient-to-br from-red-600 to-pink-600 text-white text-lg">
                  {artist.name.charAt(0).toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <span className="absolute -top-1 -right-1 flex h-4 w-4">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-4 w-4 bg-red-500 items-center justify-center">
                  <Radio className="h-2 w-2 text-white" />
                </span>
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <h3 className="font-semibold text-white truncate">{artist.name}</h3>
                {artist.isVerified && (
                  <CheckCircle2 className="h-4 w-4 text-violet-400 shrink-0" />
                )}
              </div>
              <p className="text-sm text-zinc-500 truncate">@{artist.handle}</p>
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="border-red-500/30 text-red-400 bg-red-500/10">
                {t('liveBadge')}
              </Badge>
              <span className="text-sm text-white truncate">{event.title}</span>
            </div>
            {event.venue && (
              <div className="flex items-center gap-1.5 text-sm text-zinc-400">
                <MapPin className="h-3.5 w-3.5" />
                <span className="truncate">{event.venue}</span>
              </div>
            )}
            <div className="flex items-center gap-4 text-xs text-zinc-500">
              <div className="flex items-center gap-1">
                <Users className="h-3.5 w-3.5" />
                <span>{event.viewerCount} {t('watching')}</span>
              </div>
              <div className="flex items-center gap-1">
                <Heart className="h-3.5 w-3.5" />
                <span>{event.reactionCount} {t('reactions')}</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </Link>
  )
}

// Featured Artist Card
function FeaturedArtistCard({ 
  artist 
}: { 
  artist: {
    id: string
    name: string
    handle: string
    bio: string | null
    profileImageUrl: string | null
    isVerified: boolean
    isLive: boolean
    eventCount: number
    songCount: number
  }
}) {
  const t = useTranslations('discover')
  return (
    <Link href={`/${artist.handle}`}>
      <Card className={cn(
        "border-zinc-800 bg-zinc-900/50 hover:bg-zinc-800/50 transition-all duration-300 group cursor-pointer h-full",
        artist.isLive && "border-red-500/30"
      )}>
        <CardContent className="p-4">
          <div className="flex flex-col items-center text-center">
            <div className="relative mb-3">
              <Avatar className="h-16 w-16 ring-2 ring-zinc-700 group-hover:ring-violet-500/50 transition-all">
                <AvatarImage src={artist.profileImageUrl || getPlaceholderProfileUrl(artist.id, artist.name)} />
                <AvatarFallback className="bg-gradient-to-br from-violet-600 to-fuchsia-600 text-white text-xl">
                  {artist.name.charAt(0).toUpperCase()}
                </AvatarFallback>
              </Avatar>
              {artist.isLive && (
                <span className="absolute -top-1 -right-1 flex h-4 w-4">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-4 w-4 bg-red-500"></span>
                </span>
              )}
            </div>
            <div className="flex items-center gap-1.5 mb-1">
              <h3 className="font-semibold text-white truncate">{artist.name}</h3>
              {artist.isVerified && (
                <CheckCircle2 className="h-4 w-4 text-violet-400 shrink-0" />
              )}
            </div>
            <p className="text-xs text-zinc-500 mb-2">@{artist.handle}</p>
            <div className="flex items-center gap-3 text-xs text-zinc-500">
              <span>{t('shows', { count: artist.eventCount })}</span>
              <span>•</span>
              <span>{t('songs', { count: artist.songCount })}</span>
            </div>
            {artist.isLive && (
              <Badge variant="outline" className="mt-2 border-red-500/30 text-red-400 bg-red-500/10 text-xs">
                {t('liveNowBadge')}
              </Badge>
            )}
          </div>
        </CardContent>
      </Card>
    </Link>
  )
}

